import { inject } from '@angular/core';

import { injectMutation } from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';
import { Store } from '@ngrx/store';

import { Dashboard } from 'src/app/core/models/core.models';
import { updateDashboardSuccess } from 'src/app/core/state/core.actions';
import { WorkspaceObjectsService } from 'src/app/core/services/workspace-objects.service';
import {
  CopyDashboardParams,
  CopyDashboardResponse,
  DashboardApiService,
} from '../services/dashboard-api.service';

/**
 * Duplicate one or more dashboards. The hook owns the workspace-objects cache
 * append - the copies land in their workspace's cached list, no refetch.
 */
export function injectDuplicateDashboardsMutation() {
  const dashboardApi = inject(DashboardApiService);
  const workspaceObjects = inject(WorkspaceObjectsService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (copyDashboardParams: CopyDashboardParams) =>
      firstValueFrom(dashboardApi.copyDashboard(copyDashboardParams)),
    onSuccess: (response: CopyDashboardResponse) => {
      workspaceObjects.appendDashboards(response.dashboards);

      toast.showSuccess(
        translate.instant('msg.dashboard.duplicate-success', {
          count: response?.dashboards?.length ?? 0,
        }),
      );
    },
  }));
}

/**
 * Create a dashboard. The hook owns the workspace-objects cache append. A failed
 * create is silent by design.
 */
export function injectCreateDashboardMutation() {
  const dashboardApi = inject(DashboardApiService);
  const workspaceObjects = inject(WorkspaceObjectsService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (dashboard: Record<string, unknown>) =>
      firstValueFrom(dashboardApi.createDashboard(dashboard)),
    onSuccess: (dashboard: Dashboard) => {
      workspaceObjects.appendDashboard(dashboard);

      toast.showSuccess(
        translate.instant(
          'dashboard-editor-popup.msg.dashboard-created-successfully',
        ),
      );
    },
  }));
}

/**
 * Update a dashboard. The hook owns the workspace-objects cache replace and still
 * broadcasts `updateDashboardSuccess` for `ObjectState.object`, which preserves
 * `selected_page_id` across the update.
 */
export function injectUpdateDashboardMutation() {
  const dashboardApi = inject(DashboardApiService);
  const workspaceObjects = inject(WorkspaceObjectsService);
  const store = inject(Store);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({
      id,
      dashboard,
    }: {
      id: string;
      dashboard: Record<string, unknown>;
    }) => firstValueFrom(dashboardApi.updateDashboard(id, dashboard)),
    onSuccess: (dashboard: Dashboard) => {
      workspaceObjects.replaceDashboard(dashboard);
      store.dispatch(updateDashboardSuccess({ dashboard }));

      toast.showSuccess(
        translate.instant(
          'dashboard-editor.msg.dashboard-updated-successfully',
        ),
      );
    },
  }));
}
